import React, { Suspense, useMemo } from 'react';
import { useWorldStore } from '../../state/worldStore';
import { useBodyStore, mannequinRig } from '../../game/mannequins';
import AnimatedResident from '../AnimatedResident';
import SafeMount from '../SafeMount';
import { useHeliTour, usePilots, pilotFor } from '../../city/heliTour';
import { CABIN_SEATS, PILOT_SEAT } from './HeliDirector';

// THE PEOPLE IN A HELICOPTER (2026-11-01). Khari: "I'd like to be able to see
// the passengers I am riding with. they are currently invisible during the
// ride."
//
// Whoever is aboard a pad's helicopter (the server's `seats`, in boarding order)
// sits in it — in the same seat HeliDirector puts their camera in
// (CABIN_SEATS), in their own body: a player's chosen mannequin (the one others
// see them walk in, game/mannequins), strapped in (poses.js `passenger`). The pad's
// pilot (city/heliTour usePilots — a cast member, once their body is built) has the
// front right seat.
//
// Drawn INSIDE the helicopter's posed group (Helicopter `children`), in its kit
// space, so they bank and turn with it — and so everyone in the street sees who
// is flying, not an empty helicopter. Their bodies on the ground are hidden
// while they are aboard (Peers, PlayerBody). Yours is left out only when you
// are looking out through your own eyes (the window seat, in the air).
const SEAT_ROOT_Y = 0.46;    // kit: the sitting pose drops the hips ~¼ of a body onto a cushion at y≈0.69
const HIP_BACK = 0.06;       // the hips sit a hand forward of the seat back (the seat's camera z is the eye)

function SeatedBody({ rig, seat, avatarId = null }) {
  return (
    <group position={[seat.x, SEAT_ROOT_Y, seat.z - HIP_BACK]}>
      <SafeMount key={rig.rigged} label={`heli passenger ${rig.key || avatarId || ''}`} retryMs={30000}>
        <Suspense fallback={null}>
          <AnimatedResident
            meshUrl={rig.rigged} rigPose={rig.pose || null} clipMap={rig.clips || null}
            pose="passenger" avatarId={avatarId} position={[0, 0, 0]} rotationY={0} height={rig.height || 0.85}
          />
        </Suspense>
      </SafeMount>
    </group>
  );
}

export default function HeliPassengers({ padId }) {
  const pad = useHeliTour((s) => s.pads[padId]);
  const myId = useHeliTour((s) => s.myId);
  const offline = useHeliTour((s) => s.offline);
  const view = useHeliTour((s) => s.view);
  const mine = useHeliTour((s) => s.mine?.pad === padId);
  const presences = useWorldStore((s) => s.presences);
  const npcList = useWorldStore((s) => s.npcList);
  const myBody = useBodyStore((s) => s.body);
  const pilotId = usePilots((s) => pilotFor(padId, s)?.avatarId || null);
  const pilot = useMemo(() => {
    const b = pilotId ? (npcList || []).find((n) => Number(n?.avatarId) === pilotId && n.rigged && !n.test) : null;
    return b ? { rigged: b.rigged, pose: b.pose || null, clips: b.clips || null, height: b.height || 0.75 } : null;
  }, [npcList, pilotId]);

  const aboard = pad && pad.state !== 'idle' ? (pad.seats || []) : [];
  const looking = mine && view === 'cabin' && pad?.state === 'flying';
  const bodies = aboard.slice(0, CABIN_SEATS.length).map((s, i) => {
    const me = offline ? i === 0 : s?.id && s.id === myId;
    if (me && looking) return null;                 // you are the camera
    const key = me ? myBody : (presences || []).find((p) => p.id === s?.id)?.body;
    const rig = mannequinRig(key);
    return rig ? <SeatedBody key={s?.id || i} rig={rig} seat={CABIN_SEATS[i]} /> : null;
  });
  return (
    <>
      {bodies}
      {pilot && pad && pad.state !== 'idle' && <SeatedBody key={`pilot:${pilotId}`} rig={pilot} seat={PILOT_SEAT} avatarId={pilotId} />}
    </>
  );
}
